import { Delete, Edit, Info } from '@mui/icons-material';
import { Box, Chip, useMediaQuery, useTheme } from '@mui/material';
import type { GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionButton, ActionButtonLink } from '~/components/button';
import { hiddenHeader } from '~/components/data-grid';
import { AuthorizedLink } from '~/components/links';
import { useLink } from '~/hooks/link';
import { formatDateOnly } from '~/lib/datetime';
import { getScheduleDescription, isExpired } from '~/lib/schedules';
import type { ScheduledShows__ScheduledShow as ScheduledShow } from '../../queries/queries.generated';

export interface UseColumnsProps {
  onDelete: (scheduledShow: ScheduledShow) => void;
  onUpdate: (scheduledShow: ScheduledShow) => void;
}

export const useColumns = ({ onDelete, onUpdate }: UseColumnsProps): GridColDef[] => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallAndUp = useMediaQuery(theme.breakpoints.up('sm'));

  const link = useLink();

  return useMemo(
    () => [
      {
        field: 'name',
        flex: 0.15,
        headerName: t('common.labels.name'),
        renderCell: ({ row }: GridRenderCellParams<ScheduledShow>) => row.name,
      },
      {
        field: 'label',
        flex: 0.125,
        ...hiddenHeader(t('common.labels.status')),
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams<ScheduledShow>) => {
          return (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {row.schedule?.allDay && (
                <Chip color="primary" label={t('pages.schedules.list.labels.all_day')} />
              )}
              {isExpired(row.schedule?.rrule) && (
                <Chip color="error" label={t('pages.schedules.list.labels.expired')} />
              )}
            </Box>
          );
        },
      },
      {
        field: 'show',
        flex: 0.15,
        headerName: t('pages.schedules.list.columns.show'),
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams<ScheduledShow>) => (
          <AuthorizedLink
            authorized={row.canUpdate.value && !row.show.originId}
            entity={row.show}
          />
        ),
      },
      {
        field: 'start',
        headerName: t('pages.schedules.list.columns.start'),
        minWidth: 150,
        renderCell: ({ row }: GridRenderCellParams<ScheduledShow>) =>
          row.schedule?.startDate ? formatDateOnly(row.schedule.startDate, { zone: 'UTC' }) : '',
        sortable: false,
      },
      {
        field: 'schedule',
        headerName: t('pages.schedules.list.columns.schedule'),
        sortable: false,
        flex: 0.25,
        renderCell: ({
          value: schedule,
        }: GridRenderCellParams<ScheduledShow, ScheduledShow['schedule']>) => {
          return schedule ? getScheduleDescription(schedule, t) : '';
        },
      },
      {
        field: 'devices',
        headerName: t('common.labels.devices'),
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams<ScheduledShow>) => {
          return <Chip color="primary" label={row.deviceCount || 0} />;
        },
      },
      {
        field: 'actions',
        type: 'actions',
        align: 'right',
        minWidth: isSmallAndUp ? 150 : 0,
        getActions: ({ row }: GridRowParams<ScheduledShow>) => {
          return [
            <ActionButtonLink
              key={`schedule-details-${row.id}`}
              title={t('pages.schedules.list.actions.schedule_details')}
              Icon={Info}
              to={link(`/schedules/${String(row.id)}/details`)}
            />,
            <ActionButton
              disabled={!row.canUpdate.value}
              key={`schedule-edit-${row.id}`}
              title={
                row.canUpdate.value
                  ? t('pages.schedules.list.actions.edit_schedule')
                  : row.canUpdate.reasons?.fullMessages[0] ?? ''
              }
              Icon={Edit}
              onClick={() => onUpdate(row)}
            />,
            <ActionButton
              color="error"
              key={`schedule-delete-${row.id}`}
              title={t('pages.schedules.list.actions.delete_schedule')}
              Icon={Delete}
              onClick={() => onDelete(row)}
            />,
          ];
        },
      },
    ],
    [isSmallAndUp, onDelete, link, onUpdate, t],
  );
};
